/* Bulk-enable canteen/transport for a class + CSV import section.
 * Extracted from Students.js (2026-02 refactor). */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Percent, FileArrowUp, FileArrowDown } from '@phosphor-icons/react';

export default function BulkServicesDialog({
  open, onOpenChange, form, setForm, classes, transportZones,
  onSubmit, saving, bulkResult,
  csvDownloading, onDownloadCsvTemplate,
  csvUploading, onCsvUpload, csvResult,
  language,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bulk-services-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            <Percent size={22} className="text-amber-600" />
            {language === 'fr' ? 'Activer services en masse' : 'Bulk enable services'}
          </DialogTitle>
          <DialogDescription className="font-manrope">
            {language === 'fr'
              ? 'Active la cantine et/ou le transport pour tous les élèves d\'une classe en une opération. Réservé Admin.'
              : 'Enable canteen and/or transport for all students of a class at once. Admin-only.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="font-manrope">{language === 'fr' ? 'Classe' : 'Class'}</Label>
            <Select value={form.class_name} onValueChange={(v) => setForm({ ...form, class_name: v })}>
              <SelectTrigger data-testid="bulk-class-select">
                <SelectValue placeholder={language === 'fr' ? 'Sélectionner une classe' : 'Pick a class'} />
              </SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c.name || c.class_id || c} value={c.name || c.class_id || c}>
                    {c.name || c.class_id || c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 border border-[#E2E8F0] rounded-md p-3 bg-amber-50/30">
            <label className="flex items-center gap-2 font-manrope text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.has_canteen}
                onChange={(e) => setForm({ ...form, has_canteen: e.target.checked })}
                data-testid="bulk-canteen-toggle" className="w-4 h-4"
              />
              {language === 'fr' ? 'Activer la cantine' : 'Enable canteen'}
            </label>

            <label className="flex items-center gap-2 font-manrope text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.has_transport}
                onChange={(e) => setForm({
                  ...form,
                  has_transport: e.target.checked,
                  transport_zone: e.target.checked ? form.transport_zone : '',
                })}
                data-testid="bulk-transport-toggle" className="w-4 h-4"
              />
              {language === 'fr' ? 'Activer le transport' : 'Enable transport'}
            </label>

            {form.has_transport && (
              <div className="pl-6 space-y-1">
                <Label className="text-xs font-manrope">{language === 'fr' ? 'Zone de transport' : 'Transport zone'}</Label>
                <Select value={form.transport_zone} onValueChange={(v) => setForm({ ...form, transport_zone: v })}>
                  <SelectTrigger data-testid="bulk-transport-zone">
                    <SelectValue placeholder={language === 'fr' ? 'Choisir une zone…' : 'Pick a zone…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {transportZones.map((z) => (
                      <SelectItem key={z.key} value={z.key}>
                        {z.name} ({new Intl.NumberFormat('fr-FR').format(z.amount || 0)} FCFA)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 font-manrope text-xs text-[#64748B] cursor-pointer">
            <input
              type="checkbox"
              checked={form.override_existing}
              onChange={(e) => setForm({ ...form, override_existing: e.target.checked })}
              data-testid="bulk-override-toggle" className="w-4 h-4"
            />
            {language === 'fr'
              ? 'Forcer la mise à jour même pour les élèves déjà configurés différemment'
              : 'Override students already set differently'}
          </label>

          {bulkResult && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm font-manrope" data-testid="bulk-result">
              <div><strong>{language === 'fr' ? 'Classe' : 'Class'}:</strong> {bulkResult.class_name}</div>
              <div><strong>{language === 'fr' ? 'Total élèves' : 'Total students'}:</strong> {bulkResult.total_students}</div>
              <div className="text-emerald-700"><strong>{language === 'fr' ? 'Mis à jour' : 'Updated'}:</strong> {bulkResult.updated}</div>
              <div className="text-[#64748B]"><strong>{language === 'fr' ? 'Ignorés' : 'Skipped'}:</strong> {bulkResult.skipped}</div>
              {bulkResult.operation_id && (
                <a
                  href="/admin/bulk-operations"
                  className="inline-block mt-2 text-xs text-red-600 hover:underline"
                  data-testid="bulk-undo-link"
                >
                  {language === 'fr' ? '↶ Annuler cette opération (Historique)' : '↶ Undo this operation (History)'}
                </a>
              )}
            </div>
          )}

          {/* CSV import section */}
          <div className="border-t border-[#E2E8F0] pt-4 space-y-3">
            <div className="font-outfit font-semibold text-sm flex items-center gap-2">
              <FileArrowUp size={16} className="text-indigo-600" />
              {language === 'fr' ? 'Ou importer un CSV (toutes classes)' : 'Or import a CSV (all classes)'}
            </div>
            <p className="text-xs font-manrope text-[#64748B]">
              {language === 'fr'
                ? 'Téléchargez le modèle pré-rempli avec vos élèves, modifiez les colonnes has_canteen / has_transport / transport_zone, puis ré-uploadez.'
                : 'Download the template pre-filled with your students, edit has_canteen / has_transport / transport_zone columns, then re-upload.'}
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button" variant="outline" size="sm"
                onClick={onDownloadCsvTemplate} disabled={csvDownloading}
                data-testid="csv-template-btn"
              >
                <FileArrowDown size={14} className="mr-1" />
                {csvDownloading
                  ? (language === 'fr' ? 'Génération…' : 'Generating…')
                  : (language === 'fr' ? 'Télécharger le modèle' : 'Download template')}
              </Button>
              <label className="cursor-pointer">
                <input
                  type="file" accept=".csv"
                  onChange={onCsvUpload} className="hidden"
                  data-testid="csv-upload-input"
                />
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-sm font-manrope">
                  <FileArrowUp size={14} />
                  {csvUploading
                    ? (language === 'fr' ? 'Import…' : 'Uploading…')
                    : (language === 'fr' ? 'Importer un CSV' : 'Upload a CSV')}
                </span>
              </label>
            </div>

            {csvResult && (
              <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-xs font-manrope space-y-1" data-testid="csv-result">
                <div><strong>{language === 'fr' ? 'Lignes totales' : 'Total rows'}:</strong> {csvResult.rows_total}</div>
                <div className="text-emerald-700"><strong>{language === 'fr' ? 'Mis à jour' : 'Updated'}:</strong> {csvResult.updated}</div>
                <div className="text-[#64748B]"><strong>{language === 'fr' ? 'Ignorés' : 'Skipped'}:</strong> {csvResult.skipped}</div>
                {csvResult.error_count > 0 && (
                  <details className="text-red-700">
                    <summary className="cursor-pointer">
                      <strong>{language === 'fr' ? 'Erreurs' : 'Errors'}:</strong> {csvResult.error_count}
                    </summary>
                    <ul className="pl-4 mt-1 list-disc max-h-32 overflow-y-auto">
                      {csvResult.errors.map((e, i) => (
                        <li key={i}>L.{e.row} — {e.error} {e.student_id ? `(${e.student_id})` : ''}</li>
                      ))}
                    </ul>
                  </details>
                )}
                {csvResult.operation_id && (
                  <a
                    href="/admin/bulk-operations"
                    className="inline-block mt-1 text-xs text-red-600 hover:underline"
                    data-testid="csv-undo-link"
                  >
                    {language === 'fr' ? '↶ Annuler cet import (Historique)' : '↶ Undo this import (History)'}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline" onClick={() => onOpenChange(false)}
            disabled={saving} data-testid="bulk-cancel-btn"
          >
            {language === 'fr' ? 'Fermer' : 'Close'}
          </Button>
          <Button
            onClick={onSubmit} disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="bulk-confirm-btn"
          >
            {saving
              ? (language === 'fr' ? 'Application…' : 'Applying…')
              : (language === 'fr' ? 'Appliquer' : 'Apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
